import React, { useState, useMemo, useCallback } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Tooltip, IconButton, makeStyles } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";

const useStyles = makeStyles((theme) => ({
  copyButton: {
    padding: 4,
    opacity: 0,
    transition: "opacity 0.2s",
    backgroundColor: "transparent",
    "&:hover": {
      opacity: 1,
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  copySuccess: {
    color: theme.palette.success.main,
  },
  cellWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    "&:hover $copyButton": {
      opacity: 0.8,
    },
  },
  cellContent: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    userSelect: "text",
    paddingLeft: 10,
  },
}));
// Formats different types of cell data for copying
export const formatCellContent = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
};

export const CopyButton = React.memo(
  ({ value, tooltipText = "Copy to clipboard" }) => {
    const classes = useStyles();
    const [copied, setCopied] = useState(false);

    // Memoize the formatted text so it only recomputes when "value" changes
    const textToCopy = useMemo(() => formatCellContent(value), [value]);

    // useCallback to ensure handleCopy doesn't get re-created on every render
    const handleCopy = useCallback(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }, []);

    if (!textToCopy) return null;

    return (
      <CopyToClipboard text={textToCopy} onCopy={handleCopy}>
        <Tooltip
          title={copied ? "Copied!" : tooltipText}
          enterDelay={500}
          leaveDelay={0}
          placement="top"
        >
          <IconButton className={classes.copyButton} size="small">
            {copied ? (
              <CheckIcon fontSize="small" className={classes.copySuccess} />
            ) : (
              <FileCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </CopyToClipboard>
    );
  }
);

// A cell renderer wrapper that adds copy functionality
export const CellWithCopyButton = React.memo(({ children, value }) => {
  const classes = useStyles();

  return (
    <div className={classes.cellWrapper}>
      <div className={classes.cellContent}>{children}</div>
      <CopyButton value={value} />
    </div>
  );
});

// Helper function to create cell renderers with copy functionality
export const createCellRendererWithCopy = (renderFunc) => {
  // Wrap the returned component in React.memo to prevent unnecessary renders
  return React.memo((params) => {
    const content = renderFunc ? renderFunc(params) : params.value;
    return (
      <CellWithCopyButton value={params.value}>{content}</CellWithCopyButton>
    );
  });
};
